import React, { useState, useEffect } from 'react';
import {
  getTutorialVideosAdmin,
  createTutorialVideo,
  updateTutorialVideo,
  deleteTutorialVideo,
} from '../../services/api';

const TIER_OPTIONS = ['free', 'paid'];

const SECTION_KEY_OPTIONS = [
  { value: 'watchlist',        label: 'Watchlist' },
  { value: 'screener',         label: 'Screener' },
  { value: 'stock_analyzer',   label: 'Stock Analyzer' },
  { value: 'dashboard_builder',label: 'Dashboard Builder' },
];

function toEmbedUrl(raw) {
  const url = raw.trim();
  if (url.includes('/embed/') || url.includes('player.vimeo.com')) return url;

  const ytMatch = url.match(/(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?rel=0&modestbranding=1&autoplay=1`;
  }

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&title=0&byline=0&portrait=0`;
  }

  return url;
}

const emptyForm = {
  section_key: '',
  title: '',
  description: '',
  embed_url: '',
  tier: 'free',
  is_active: true,
};

export default function VideoManager() {
  const [videos, setVideos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchVideos();
  }, []);

  async function fetchVideos() {
    setLoading(true);
    setError('');
    try {
      const data = await getTutorialVideosAdmin();
      setVideos(data);
    } catch {
      setError('Failed to load videos.');
    } finally {
      setLoading(false);
    }
  }

  function startEdit(video) {
    setEditingId(video.id);
    setForm({
      section_key: video.section_key,
      title: video.title,
      description: video.description || '',
      embed_url: video.embed_url,
      tier: video.tier,
      is_active: video.is_active,
    });
    setError('');
    setSuccess('');
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const payload = { ...form, embed_url: toEmbedUrl(form.embed_url) };
      if (editingId) {
        await updateTutorialVideo(editingId, payload);
        setSuccess('Video updated.');
      } else {
        await createTutorialVideo(payload);
        setSuccess('Video added.');
      }
      setEditingId(null);
      setForm(emptyForm);
      await fetchVideos();
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const msgs = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
        setError(msgs.join(' | '));
      } else {
        setError('Save failed. Check the form and try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this video entry?')) return;
    try {
      await deleteTutorialVideo(id);
      setSuccess('Deleted.');
      await fetchVideos();
    } catch {
      setError('Delete failed.');
    }
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fdfdfd', marginBottom: 24 }}>
        Tutorial Video Manager
      </h2>

      {/* Add / Edit Form */}
      <div style={{
        background: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 32,
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: '#fdfdfd', marginBottom: 16 }}>
          {editingId ? 'Edit Video' : 'Add New Video'}
        </h3>

        {error && (
          <div style={{ background: '#2a1515', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#0f2a1a', border: '1px solid #10b981', borderRadius: 8, padding: '10px 14px', color: '#10b981', fontSize: 13, marginBottom: 14 }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={labelStyle}>
              Section Key
              <select
                style={inputStyle}
                value={form.section_key}
                onChange={e => setForm(f => ({ ...f, section_key: e.target.value }))}
                required
              >
                <option value="" disabled>Select a section…</option>
                {SECTION_KEY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label style={labelStyle}>
              Title
              <input
                style={inputStyle}
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Watchlist Tool Walkthrough"
                required
              />
            </label>
          </div>

          <label style={labelStyle}>
            Description
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Brief description shown to users in the video picker (optional)"
              rows={3}
            />
          </label>

          <label style={labelStyle}>
            Embed URL
            <input
              style={inputStyle}
              value={form.embed_url}
              onChange={e => setForm(f => ({ ...f, embed_url: e.target.value }))}
              placeholder="https://www.youtube-nocookie.com/embed/VIDEO_ID?rel=0&modestbranding=1&autoplay=1"
              required
            />
            <span style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              Paste any YouTube or Vimeo link — watch URLs are auto-converted to embed format.
            </span>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <label style={labelStyle}>
              Tier
              <select
                style={inputStyle}
                value={form.tier}
                onChange={e => setForm(f => ({ ...f, tier: e.target.value }))}
              >
                {TIER_OPTIONS.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </label>
            <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 10, paddingTop: 20 }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: '#218aff' }}
              />
              <span style={{ color: '#aeb9cc', fontSize: 14 }}>Active (shows button to users)</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="submit" disabled={saving} style={primaryBtnStyle}>
              {saving ? 'Saving...' : editingId ? 'Update Video' : 'Add Video'}
            </button>
            {editingId && (
              <button type="button" onClick={cancelEdit} style={secondaryBtnStyle}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Video List */}
      <div style={{
        background: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #333' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#fdfdfd', margin: 0 }}>
            Configured Videos ({videos.length})
          </h3>
        </div>

        {loading ? (
          <div style={{ padding: 24, color: '#666', textAlign: 'center' }}>Loading...</div>
        ) : videos.length === 0 ? (
          <div style={{ padding: 24, color: '#666', textAlign: 'center' }}>
            No videos configured yet. Add one above.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#111' }}>
                {['Section Key', 'Title', 'Tier', 'Active', 'Actions'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {videos.map((v, i) => (
                <tr key={v.id} style={{ background: i % 2 === 0 ? '#1a1a1a' : '#181818' }}>
                  <td style={tdStyle}>
                    <code style={{ color: '#218aff', fontSize: 12 }}>{v.section_key}</code>
                  </td>
                  <td style={tdStyle}>{v.title}</td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11,
                      background: v.tier === 'paid' ? '#1a1520' : '#0f1a12',
                      color: v.tier === 'paid' ? '#a78bfa' : '#10b981',
                      border: `1px solid ${v.tier === 'paid' ? '#6d28d9' : '#065f46'}`,
                    }}>
                      {v.tier}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: v.is_active ? '#10b981' : '#666' }}>
                      {v.is_active ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => startEdit(v)} style={ghostBtnStyle}>Edit</button>
                      <button onClick={() => handleDelete(v.id)} style={{ ...ghostBtnStyle, color: '#ef4444' }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 13,
  color: '#aeb9cc',
};

const inputStyle = {
  background: '#111',
  border: '1px solid #444',
  borderRadius: 8,
  color: '#fdfdfd',
  fontSize: 13,
  padding: '8px 12px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const primaryBtnStyle = {
  background: '#218aff',
  border: 'none',
  borderRadius: 8,
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  padding: '9px 20px',
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  background: 'transparent',
  border: '1px solid #444',
  borderRadius: 8,
  color: '#aeb9cc',
  fontSize: 13,
  padding: '9px 20px',
  cursor: 'pointer',
};

const ghostBtnStyle = {
  background: 'transparent',
  border: '1px solid #333',
  borderRadius: 6,
  color: '#aeb9cc',
  fontSize: 12,
  padding: '4px 10px',
  cursor: 'pointer',
};

const thStyle = {
  textAlign: 'left',
  padding: '10px 16px',
  color: '#666',
  fontWeight: 500,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle = {
  padding: '10px 16px',
  color: '#fdfdfd',
  borderTop: '1px solid #222',
};
